import {createStaticHero} from './hero-static.js';
const hero = document.querySelector('[data-lp-product-hero]');
if (hero) {
  const container = hero.querySelector('[data-lp-hero-stage]');
  const products = JSON.parse(hero.querySelector('[data-lp-hero-products]').textContent);
  const byId = new Map(products.map(product => [product.id, product]));
  const touch = matchMedia('(hover:none)');
  const hotspots = [...hero.querySelectorAll('[data-lp-hotspot]')];
  const links = [...hero.querySelectorAll('[data-lp-product-link]')];
  const label = hero.querySelector('[data-lp-model-label]');
  const detailImage = hero.querySelector('[data-lp-detail-image]');
  const detailLink = hero.querySelector('[data-lp-detail-link]');
  const instruction = hero.querySelector('[data-lp-hero-instructions]');
  const controllers = new AbortController(), events = {signal:controllers.signal};
  let scene = null, active = null, projection = [], loading = false, dead = false;

  function project(points) {
    projection = points;
    for (const point of points) {
      const hotspot = hotspots.find(link => link.dataset.lpHotspot === point.id);
      if (!hotspot) continue;
      hotspot.style.left = `${point.x}px`; hotspot.style.top = `${point.y}px`; hotspot.hidden = !point.visible;
    }
    positionLabel();
  }
  function positionLabel() {
    const point = projection.find(point => point.id === active);
    label.hidden = !scene || !point?.visible || !active;
    if (label.hidden) return;
    const width = label.offsetWidth, height = label.offsetHeight, gap = 20, padding = 8;
    const markers = projection.filter(p => p.visible);
    const candidates = [
      [point.x - width / 2, point.y - height - gap],
      [point.x - width / 2, point.y + gap],
      [point.x + gap, point.y - height / 2],
      [point.x - width - gap, point.y - height / 2],
      // Narrow screens can use a free gap between another pair of product rows.
      ...markers.flatMap(p => [[point.x - width / 2, p.y + gap], [point.x - width / 2, p.y - height - gap]]),
    ];
    const safe = candidates.map(([x, y]) => ({
      x: Math.max(padding, Math.min(container.clientWidth - width - padding, x)),
      y: Math.max(padding, Math.min(container.clientHeight - height - padding, y)),
    })).find(({x, y}) => markers.every(p =>
      x + width < p.x - 16 || x > p.x + 16 || y + height < p.y - 16 || y > p.y + 16
    ));
    // The detail panel retains its link if there is no room for a safe overlay.
    label.hidden = !safe;
    if (safe) { label.style.left = `${safe.x}px`; label.style.top = `${safe.y}px`; }
  }
  function select(id) {
    const product = byId.get(id);
    if (!product || active === id) return;
    active = id;
    hero.dataset.activeCategory = id;
    hero.querySelector('[data-lp-detail-category]').textContent = 'LEIPOLE PRODUCT CATEGORY';
    hero.querySelector('[data-lp-detail-title]').textContent = product.name;
    hero.querySelector('[data-lp-detail-description]').textContent = product.description;
    detailImage.src = product.image; detailImage.hidden = false;
    detailLink.href = product.url; detailLink.hidden = false;
    label.href = product.url; label.setAttribute('aria-label',`Explore ${product.name}`);
    hero.querySelector('[data-lp-model-label-text]').textContent = product.name;
    for(const link of [...links,...hotspots]) link.toggleAttribute('data-active',(link.dataset.lpProductLink || link.dataset.lpHotspot) === id);
    scene?.select(id); positionLabel();
  }
  function clear() {
    active = null; delete hero.dataset.activeCategory;
    hero.querySelector('[data-lp-detail-category]').textContent = `${products.length} product categories. One connected system.`;
    hero.querySelector('[data-lp-detail-title]').textContent = 'Discover what’s inside.';
    hero.querySelector('[data-lp-detail-description]').textContent = 'Explore the components that bring an electrical enclosure together.';
    detailImage.hidden = detailLink.hidden = label.hidden = true;
    for(const link of [...links,...hotspots])link.removeAttribute('data-active');
    scene?.select(null);
  }
  function activate(id, event) {
    const product = byId.get(id); if(!product)return;
    if(event?.metaKey || event?.ctrlKey)window.open(product.url,'_blank','noopener');
    else window.location.assign(product.url);
  }
  function fallback() {
    scene?.dispose(); scene = null;
    container.classList.remove('lp-has-webgl');
    hero.querySelector('[data-lp-hotspots]').hidden = true;
    label.hidden = true;
    instruction.textContent = 'Explore the seven product categories below.';
  }
  for (const link of [...links,...hotspots]) {
    const id = link.dataset.lpProductLink || link.dataset.lpHotspot;
    link.addEventListener('pointerenter', event => {if(event.pointerType!=='touch')select(id);}, events);
    link.addEventListener('focus', () => select(id), events);
    // Model touch previews first; the seven category links navigate normally.
    // Remember selection before focus fires, since focus can precede the click.
    let selectedOnDown = null, touchDown = false;
    link.addEventListener('pointerdown', event => {selectedOnDown=active;touchDown=event.pointerType==='touch';}, events);
    link.addEventListener('click', event => {
      if(link.dataset.lpHotspot && touchDown && selectedOnDown!==id){event.preventDefault();select(id);}
      touchDown=false;
    }, events);
  }
  detailImage.addEventListener('error',()=>{detailImage.hidden=true;},events);
  hero.addEventListener('keydown',event=>{if(event.key==='Escape')clear();},events);
  container.addEventListener('lp-scene-unavailable',fallback,events);

  async function loadScene() {
    if(loading || dead)return;loading=true;
    try {
      if(dead)return;
      const created = createStaticHero(container,{products,onHover:select,onActivate:activate,onProject:project});
      if(dead){created.dispose();return;}
      scene = created;
      hero.querySelector('[data-lp-hotspots]').hidden=false;
      instruction.textContent=touch.matches?'Tap a part to preview · Tap again to open':'Hover to explore · Click to open';
      scene.select(active);
    } catch {fallback();}
  }
  const observer = new IntersectionObserver(entries=>{
    if(!entries.some(entry=>entry.isIntersecting))return;
    observer.disconnect();
    loadScene();
  },{rootMargin:'80px'});
  observer.observe(container);
  window.addEventListener('pagehide',event=>{
    if(event.persisted)return;
    dead=true;observer.disconnect();controllers.abort();scene?.dispose();
  },events);
}
